import { ContractDetailContractVersionFragment } from 'generated/resources/graphql'
import moment from 'moment'

import EmployeeContract from 'modules/contracts/lib/EmployeeContract'

const calculateContracts = (
  sortedContractVersions: ContractDetailContractVersionFragment[]
): EmployeeContract[] => {
  if (sortedContractVersions.length === 0) {
    return []
  }

  // Sort contract versions by effective date chronologically
  const contractVersionsSortedByEffectiveOn = [...sortedContractVersions].sort((a, b) => {
    const dateA = moment(a.effectiveOn)
    const dateB = moment(b.effectiveOn)
    return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0
  })

  const contractBands: EmployeeContract[] = []
  let currentBand: EmployeeContract | null = null

  contractVersionsSortedByEffectiveOn.forEach((version) => {
    const versionStartDate = moment(version.startsOn)
    const versionEndDate = version.endsOn ? moment(version.endsOn) : null

    // If no current band exists, create the first one
    if (!currentBand) {
      currentBand = new EmployeeContract(moment(version.startsOn))
      currentBand.pushVersion(version)
      if (versionEndDate) {
        currentBand.endsOn = versionEndDate
      } else {
        currentBand.endsOn = null
      }
      return
    }

    // Check if this version belongs to the current band
    const currentBandEndDate = currentBand.endsOn
    const currentBandStartDate = currentBand.startsOn

    // Special case: if both versions start on the same day but the current band ends,
    // they should be separate bands (terminated contract vs ongoing contract)
    const isSameStartDate = versionStartDate.isSame(currentBandStartDate, 'day')
    const isCurrentBandTerminated = currentBandEndDate !== null
    const isNewVersionOngoing = versionEndDate === null

    const isContinuous =
      (!currentBandEndDate ||
        versionStartDate.isSameOrBefore(currentBandEndDate.clone().add(1, 'day'))) &&
      !(isSameStartDate && isCurrentBandTerminated && isNewVersionOngoing)

    if (isContinuous) {
      // Add to current band
      currentBand.pushVersion(version)
      if (versionEndDate) {
        if (!currentBand.endsOn || versionEndDate.isAfter(currentBand.endsOn)) {
          currentBand.endsOn = versionEndDate
        }
      } else {
        // If any version has no end date, the entire contract is ongoing
        currentBand.endsOn = null
      }
    } else {
      // Start a new band
      contractBands.push(currentBand)
      currentBand = new EmployeeContract(moment(version.startsOn))
      currentBand.pushVersion(version)
      if (versionEndDate) {
        currentBand.endsOn = versionEndDate
      } else {
        currentBand.endsOn = null
      }
    }
  })

  // Add the last band
  if (currentBand) {
    contractBands.push(currentBand)
  }

  return contractBands
}

export default calculateContracts
